import { useApiMutation } from '@src/app/context/Api/hooks/useApiMutation';
import { useApiQueries } from '@src/app/context/Api/hooks/useApiQueries';
import { useApiQuery } from '@src/app/context/Api/hooks/useApiQuery';
import { apis } from '@src/shared/api';
import { isEmpty } from 'lodash';

import { queryClient } from './configuration';

/**
 * clearCache
 *
 * Clears cached queries for the specified APIs and their query keys.
 * If no arguments are provided, all cache entries are cleared.
 * If only an apiKey is provided, all queries for that API are cleared.
 * If both apiKey and queryKeys are provided, only those queries are cleared for that API.
 *
 * @param requests
 * @returns {Promise<Awaited<*|void>[]|Array<Awaited<Promise<*|void>>>|void>}
 */
export async function clearCache(requests = []) {
  if (isEmpty(requests)) {
    return queryClient.invalidateQueries();
  }

  const promises = requests.map(async ({ apiKey, queryKeys }) => {
    if (!isEmpty(queryKeys)) {
      const queryKeyPromises = queryKeys.map((queryKey) => {
        return queryClient.invalidateQueries({
          predicate: (query) => {
            return (
              (query.queryKey?.[0] === apiKey || query.queryKey?.[0]?.startsWith(apiKey)) &&
              (query.queryKey?.[1] === queryKey || query.queryKey?.[1]?.startsWith(queryKey))
            );
          },
        });
      });
      return Promise.all(queryKeyPromises);
    }

    return queryClient.invalidateQueries({
      predicate: (query) => {
        return query.queryKey?.[0] === apiKey || query.queryKey?.[0]?.startsWith(apiKey);
      },
    });
  });

  return Promise.all(promises);
}

/**
 * defaultApiContextValue
 *
 * Creates a default context value for the API context.
 *
 * @param apiKey
 * @param setApiKey
 * @returns {{queryClient: QueryClient, clearApiCache: ((function([]=): (Promise<Awaited<void>[]>|Promise<void>))|*),
 *     mutations: *, apiKey, useApiQuery: (function(*, *): *[]), setApiKey, useApiMutation: (function(*, *): *[]),
 *     useApiQueries: (function(*): {}), queries: *}}
 */
export function defaultApiContextValue(apiKey, setApiKey) {
  return {
    apiKey,
    clearApiCache: clearCache,
    mutations: apis?.[apiKey]?.mutations,
    queries: apis?.[apiKey]?.queries,
    queryClient,
    setApiKey,
    useApiMutation: (mutationKey, mutationOptions) => {
      return useApiMutation(apiKey, mutationKey, mutationOptions);
    },
    useApiQueries: (queries) => {
      return useApiQueries(apiKey, queries);
    },
    useApiQuery: (queryKey, queryOptions) => {
      return useApiQuery(apiKey, queryKey, queryOptions);
    },
  };
}
